import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { SpeechRecognizer } from 'microsoft-cognitiveservices-speech-sdk';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { SpeechRecogniserService } from '@omni/services/speech/speech-recogniser.service';
import { AssistantAttachment } from '@omni/ai/features/assistant-attachment';
import { AgentRegistryService } from '@omni/ai/services/agent-registry.service';
import { AgentCategory, AgentSkill } from '@omni/ai/models/agent-config.model';
import { matchSkills, slashQuery } from '@omni/ai/features/skill-commands';

/**
 * Shared chat input strip with a mic on the LEFT of the field. Two states:
 *
 *  - `idle` — mic button (left) | text field | send button (right)
 *  - `listening` — pulsating Stop button (left) | text field showing the
 *     transcript streaming in live | duration + cancel (right)
 *
 * During listening, recognized text is appended directly into `draft` so the
 * user sees their words appear in real time. A snapshot of the draft at the
 * moment listening starts lets cancel revert the field cleanly; stop keeps
 * the appended text in place so the user can edit before sending.
 */
@Component({
  selector: 'nova-mic-input',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TranslateModule],
  templateUrl: './nova-mic-input.component.html',
  styleUrls: ['./nova-mic-input.component.scss'],
})
export class NovaMicInputComponent implements OnDestroy {
  /** Placeholder for the idle field, already translated by the host; empty shows the default Ask-Nova prompt. */
  @Input() placeholder = '';
  @Input() isSending: boolean = false;
  /**
   * True while the device has no connection: an Offline label shows above the field, Send and Attach
   * are disabled, and the field says why.
   */
  @Input() isOffline: boolean = false;
  @Input() attachments: AssistantAttachment[] = [];

  trackByKindId(_i: number, a: AssistantAttachment): string { return a.kind + ':' + a.id; }

  @Output() sendMessage = new EventEmitter<string>();
  @Output() voiceError  = new EventEmitter<string>();
  @Output() attachClick = new EventEmitter<void>();
  @Output() removeAttachment = new EventEmitter<AssistantAttachment>();

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  draft = '';
  state: 'idle' | 'listening' = 'idle';
  durationLabel = '00:00';

  /** Skills matching the `/` token being typed. Non-empty only while the menu is open. */
  slashResults: AgentSkill[] = [];
  /** Row the keyboard is on; Enter and Tab commit this one. */
  slashIndex = 0;
  slashOpen = false;

  readonly #authService = inject(AuthenticationService);
  readonly #zone = inject(NgZone);
  readonly #deviceService = inject(DeviceService);
  readonly #registry = inject(AgentRegistryService);
  readonly #recognisers = inject(SpeechRecogniserService);
  readonly #translate = inject(TranslateService);

  /** Mic button is rendered only when speech is configured AND the device is online. */
  get micAvailable(): boolean {
    return !!this.#authService.speechConfig?.speechSubscriptionKey
      && !this.#deviceService.isOffline;
  }

  /** The label shown above the field while the device has no connection. */
  get offlineLabel(): string {
    return this.#translate.instant('OFFLINE');
  }

  /**
   * The idle field's prompt: the offline notice while the device has no connection, else
   * `placeholder`, else the default Ask-Nova prompt.
   */
  get fieldPlaceholder(): string {
    if (this.isOffline) return this.#translate.instant('AI_ASSISTANT_OFFLINE_PLACEHOLDER');
    return this.placeholder || this.#translate.instant('NOVA_INPUT_PLACEHOLDER');
  }

  /** What the rep had already typed before starting voice input — used for cancel. */
  #draftSnapshot = '';
  /** Accumulated voice-recognized text appended onto the snapshot during this listening pass. */
  #recognized = '';
  #recordingStartMs = 0;
  #tickHandle: ReturnType<typeof setInterval> | null = null;
  #recognizer: SpeechRecognizer | null = null;

  // ── Idle ───────────────────────────────────────────────────────────────────

  /**
   * Emits the trimmed draft as {@link sendMessage} and clears the field. While the device is
   * offline the draft stays in the field, because the harness does not send it; the rep can send
   * it again once connected.
   */
  submit(): void {
    // The `keyup.enter` binding fires even when keydown already committed a skill row, so an
    // Enter the menu consumed must not also send the message.
    if (this.#enterConsumed) {
      this.#enterConsumed = false;
      return;
    }
    const text = this.draft.trim();
    if (!text || this.isSending || this.isOffline) return;
    this.sendMessage.emit(text);
    if (!this.#deviceService.isOffline) this.draft = '';
    this.#closeSlash();
  }

  trackBySkillId(_i: number, s: AgentSkill): string { return s.id; }

  /**
   * The `/` menu is open only while the draft is a single leading token — `/`, then non-space.
   * Typing a space means the rep has committed the command and moved on to their message, so
   * the menu closes and stays closed for the rest of the line.
   */
  onDraftChange(value: string): void {
    // The new value is taken from the event rather than read back off `draft`, so this never
    // depends on which ngModelChange listener the template registered first.
    if (this.state !== 'idle' || this.isSending) return this.#closeSlash();
    const query = slashQuery(value);
    if (query === null) return this.#closeSlash();
    const skills = this.#registry.getAgent(AgentCategory.Assistant)?.skills ?? [];
    this.slashResults = matchSkills(skills, query);
    // Opened for any slash token, empty results included: a menu that silently fails to appear is
    // indistinguishable from a handler that never ran, so the empty case says so instead.
    this.slashOpen = true;
    this.slashIndex = 0;
  }

  /** Commit a row: the draft becomes `/<command> ` so the rep can keep typing their message. */
  pickSkill(skill: AgentSkill | undefined): void {
    if (!skill) return;
    this.draft = `/${skill.command} `;
    this.#closeSlash();
    this.inputEl?.nativeElement.focus();
  }

  #closeSlash(): void {
    this.slashOpen = false;
    this.slashResults = [];
    this.slashIndex = 0;
  }

  /**
   * Editor-style "@ to attach": typing `@` into an empty field opens the attach
   * picker instead of inserting the character (matches Claude Code / IDE
   * mental model). A literal `@` mid-message (e.g. an email) still types
   * normally — we only intercept when the field is empty.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === '@' && !this.draft && this.state === 'idle' && !this.isSending) {
      event.preventDefault();
      this.attachClick.emit();
      return;
    }
    if (!this.slashOpen || !this.slashResults.length) {
      if (this.slashOpen && event.key === 'Escape') { event.preventDefault(); this.#closeSlash(); }
      return;
    }
    const last = this.slashResults.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.slashIndex = this.slashIndex >= last ? 0 : this.slashIndex + 1;
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.slashIndex = this.slashIndex <= 0 ? last : this.slashIndex - 1;
        return;
      case 'Enter':
      case 'Tab':
        if (!this.slashResults.length) return;
        event.preventDefault();
        this.#enterConsumed = event.key === 'Enter';
        this.pickSkill(this.slashResults[this.slashIndex]);
        return;
      case 'Escape':
        event.preventDefault();
        this.#closeSlash();
        return;
      default:
        return;
    }
  }

  /** Set when keydown committed a row on Enter, so the paired keyup does not also submit. */
  #enterConsumed = false;

  async startListening(): Promise<void> {
    if (this.isSending || this.state === 'listening') return;
    const cfg = this.#authService.speechConfig;
    if (!cfg?.speechSubscriptionKey) {
      this.#zone.run(() => this.voiceError.emit(this.#translate.instant('NOVA_VOICE_INPUT_NOT_CONFIGURED')));
      return;
    }

    // Snapshot the current draft so the cancel button can revert it later, and
    // optimistically flip to listening so the user sees feedback before the
    // speech SDK finishes initialising.
    this.#draftSnapshot = this.draft;
    this.#recognized = '';
    this.state = 'listening';
    this.#recordingStartMs = Date.now();
    this.#updateDurationLabel();
    this.#tickHandle = setInterval(() => this.#updateDurationLabel(), 1000);

    try {
      const recogniser = await this.#recognisers.choose();
      if (this.state !== 'listening') return;
      if (!recogniser) {
        this.#zone.run(() => this.#handleVoiceError(this.#translate.instant('DICTATE_LANGUAGE_UNAVAILABLE')));
        return;
      }
      const { SpeechConfig, SpeechRecognizer, AudioConfig } = await import('microsoft-cognitiveservices-speech-sdk');
      const speechConfig = SpeechConfig.fromSubscription(recogniser.subscriptionKey, recogniser.region);
      if (recogniser.endpointId) speechConfig.endpointId = recogniser.endpointId;
      speechConfig.speechRecognitionLanguage = recogniser.language;
      const audioConfig = AudioConfig.fromDefaultMicrophoneInput();

      this.#recognizer = new SpeechRecognizer(speechConfig, audioConfig);

      this.#recognizer.recognized = (_s, e) => {
        const text = e?.result?.text?.trim();
        if (!text) return;
        this.#zone.run(() => {
          this.#recognized = this.#recognized
            ? this.#recognized.trim() + ' ' + text
            : text;
          // Show the recognized stream LIVE in the field on top of whatever
          // the user had already typed.
          const base = this.#draftSnapshot.trim();
          this.draft = base ? `${base} ${this.#recognized}` : this.#recognized;
        });
      };

      this.#recognizer.canceled = (_s, e: any) => {
        this.#recognisers.reportCancelled(recogniser);
        const details = e?.errorDetails || this.#translate.instant('NOVA_VOICE_INPUT_FAILED');
        this.#zone.run(() => this.#handleVoiceError(details));
      };

      this.#recognizer.startContinuousRecognitionAsync(
        () => { /* started — handlers wired above will do the rest */ },
        (err) => this.#zone.run(() => this.#handleVoiceError(typeof err === 'string' ? err : this.#translate.instant('NOVA_MICROPHONE_START_FAILED'))),
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : this.#translate.instant('NOVA_SPEECH_ENGINE_LOAD_FAILED');
      this.#zone.run(() => this.#handleVoiceError(msg));
    }
  }

  // ── Listening → idle (commit) ──────────────────────────────────────────────

  /**
   * Stop the recognizer and KEEP whatever was transcribed in the field so the
   * user can edit before sending. The draft is already updated live, so this
   * is just teardown + state flip.
   */
  stopListening(): void {
    if (this.state !== 'listening') return;
    const recognizer = this.#recognizer;
    const finish = () => this.#zone.run(() => {
      this.#tearDownListening();
      this.#recognized = '';
      this.#draftSnapshot = '';
      this.state = 'idle';
    });
    if (!recognizer) { finish(); return; }
    recognizer.stopContinuousRecognitionAsync(finish, finish);
  }

  // ── Listening → idle (discard) ─────────────────────────────────────────────

  /** Stop the recognizer and REVERT the field to its pre-listening contents. */
  cancelListening(): void {
    if (this.state !== 'listening') return;
    const recognizer = this.#recognizer;
    const finish = () => this.#zone.run(() => {
      this.draft = this.#draftSnapshot;
      this.#tearDownListening();
      this.#recognized = '';
      this.#draftSnapshot = '';
      this.state = 'idle';
    });
    if (!recognizer) { finish(); return; }
    recognizer.stopContinuousRecognitionAsync(finish, finish);
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    if (this.state === 'listening') {
      this.cancelListening();
    }
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  #handleVoiceError(message: string): void {
    this.voiceError.emit(message);
    this.#tearDownListening();
    // Treat an error like a cancel — don't keep half-recognized fragments.
    this.draft = this.#draftSnapshot;
    this.#recognized = '';
    this.#draftSnapshot = '';
    this.state = 'idle';
  }

  #tearDownListening(): void {
    if (this.#tickHandle) {
      clearInterval(this.#tickHandle);
      this.#tickHandle = null;
    }
    try { this.#recognizer?.close(); } catch { /* best-effort */ }
    this.#recognizer = null;
  }

  #updateDurationLabel(): void {
    const elapsed = Math.floor((Date.now() - this.#recordingStartMs) / 1000);
    const mm = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const ss = (elapsed % 60).toString().padStart(2, '0');
    this.durationLabel = `${mm}:${ss}`;
  }
}
